import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import TableHead from '@material-ui/core/TableHead';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

import { PureRecordRow } from '../../libs/utility';

function PureEnhancedTableHead(props) {
	const { columns, classes, order, orderBy, onRequestSort } = props;
	const createSortHandler = (property) => (event) => {
		onRequestSort(event, property);
	};

	return (
		<TableHead>
			<TableRow>
				{columns.map((column) => {
					let columnLabel;
					if (column.label) {
						columnLabel = column.label;
					} else if (column.labels) {
						if (column.labels.length === 1) {
							columnLabel = _.first(column.labels);
						} else {
							columnLabel = (
								<div style={{ lineHeight: 1.4 }}>
									{column.labels.map((label, i) => {
										return (
											<div key={label}>
												{label}
												{!i ? ' /' : ''}
											</div>
										);
									})}
								</div>
							);
						}
					} else {
						columnLabel = 'Missing label';
					}

					return (
						<TableCell
							key={column.sortId}
							align={column.numeric ? 'right' : 'left'}
							padding={column.disablePadding ? 'none' : 'default'}
							style={column.style}
							sortDirection={orderBy === column.sortId ? order : false}
						>
							<TableSortLabel
								active={orderBy === column.sortId}
								direction={order}
								onClick={createSortHandler(column.sortId)}
							>
								{columnLabel}
								{orderBy === column.sortId ? (
									<span className={classes.visuallyHidden}>
										{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
									</span>
								) : null}
							</TableSortLabel>
						</TableCell>
					);
				})}
			</TableRow>
		</TableHead>
	);
}

PureEnhancedTableHead.propTypes = {
	columns: PropTypes.array.isRequired,
	classes: PropTypes.object.isRequired,
	onRequestSort: PropTypes.func.isRequired,
	order: PropTypes.oneOf(['asc', 'desc']).isRequired,
	orderBy: PropTypes.string.isRequired,
};

function desc(a, b, orderBy) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

function stableSort(array, cmp) {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = cmp(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
	return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const useStyles = makeStyles(() => ({
	table: {
		width: '100%',
		tableLayout: 'auto',
		// backgroundColor : 'white',
	},
	visuallyHidden: {
		border: 0,
		clip: 'rect(0 0 0 0)',
		height: 1,
		margin: -1,
		overflow: 'hidden',
		padding: 0,
		position: 'absolute',
		top: 20,
		width: 1,
	},
}));

export default function PureEnhancedTable(props) {
	const {
		rows,
		columns,
		defaultOrder,
		defaultOrderBy,
		filterMap,
		filterId,
		isDense = false,
	} = props;

	const classes = useStyles();
	const [order, setOrder] = React.useState(defaultOrder || 'asc');
	const [orderBy, setOrderBy] = React.useState(defaultOrderBy || columns[0].sortId);

	const handleRequestSort = (event, property) => {
		const isDesc = orderBy === property && order === 'desc';
		setOrder(isDesc ? 'asc' : 'desc');
		setOrderBy(property);
	};

	return (
		<TableContainer style={{ overflowX: 'initial' }}>
			<Table
				stickyHeader
				className={classes.table}
				size={isDense ? 'small' : 'medium'}
				aria-label="enhanced table"
			>
				<PureEnhancedTableHead
					classes={classes}
					order={order}
					orderBy={orderBy}
					onRequestSort={handleRequestSort}
					columns={columns}
				/>
				<TableBody>
					{stableSort(rows, getSorting(order, orderBy)).map((row) => {
						if (filterMap && filterMap[row[filterId]]) {
							return null;
						}

						return (
							<PureRecordRow hover role="checkbox" tabIndex={-1} key={row.key}>
								{columns.map(({ displayId }) => {
									return <TableCell key={displayId}>{row[displayId]}</TableCell>;
								})}
							</PureRecordRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

PureEnhancedTable.propTypes = {
	rows: PropTypes.array.isRequired,
	columns: PropTypes.array.isRequired,
	defaultOrder: PropTypes.string,
	defaultOrderBy: PropTypes.string,
	isDense: PropTypes.bool,
	filterMap: PropTypes.object,
	filterId: PropTypes.string,
};
